/// <reference types="gtag.js" />

/**
 * Google Analytics Helper
 *
 * This file provides type-safe utility functions for tracking events
 * with Google Analytics 4 (GA4) using the gtag.js library.
 *
 * The gtag script is loaded in app.blade.php for production environment.
 */

/**
 * Check if gtag is available (production environment)
 */
const isGtagAvailable = (): boolean => {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a custom event
 *
 * @param eventName - Name of the event
 * @param eventParams - Event parameters
 */
export const trackEvent = (
    eventName: string,
    eventParams?: Gtag.EventParams | Record<string, any>
): void => {
    if (isGtagAvailable()) {
        window.gtag('event', eventName, eventParams);
        console.log('GA Event Tracked:', eventName, eventParams);
    } else {
        console.log('GA Event (dev mode):', eventName, eventParams);
    }
};

/**
 * Track user login event
 *
 * @param method - Login method (e.g., 'email', 'google', 'facebook')
 * @param userId - Optional user ID
 */
export const trackLogin = (method: string = 'email', userId?: string | number): void => {
    trackEvent('login', {
        method,
        ...(userId && { user_id: String(userId) }),
    });
};

/**
 * Track search event
 *
 * @param searchTerm - The search query
 * @param searchType - Type of search (e.g., 'course', 'document')
 * @param resultsCount - Number of results returned (optional)
 */
export const trackSearch = (
    searchTerm: string,
    searchType: 'course' | 'document' = 'course',
    resultsCount?: number
): void => {
    trackEvent('search', {
        search_term: searchTerm,
        search_type: searchType,
        ...(resultsCount !== undefined && { results_count: resultsCount }),
    });
};

/**
 * Track page view
 *
 * @param pageTitle - Title of the page
 * @param pagePath - Path of the page
 */
export const trackPageView = (pageTitle: string, pagePath: string): void => {
    if (isGtagAvailable()) {
        window.gtag('event', 'page_view', {
            page_title: pageTitle,
            page_path: pagePath,
        });
    }
};

/**
 * Track course enrollment
 *
 * @param courseId - Course ID
 * @param courseName - Course name
 */
export const trackCourseEnrollment = (courseId: number | string, courseName: string): void => {
    trackEvent('course_enrollment', {
        course_id: String(courseId),
        course_name: courseName,
    });
};

/**
 * Track document download
 *
 * @param documentId - Document ID
 * @param documentName - Document name
 */
export const trackDocumentDownload = (documentId: number | string, documentName: string): void => {
    trackEvent('document_download', {
        document_id: String(documentId),
        document_name: documentName,
    });
};

/**
 * Track custom conversion
 *
 * @param eventName - Name of the conversion event
 * @param value - Conversion value (optional)
 * @param currency - Currency code (optional)
 */
export const trackConversion = (
    eventName: string,
    value?: number,
    currency: string = 'KWD'
): void => {
    trackEvent(eventName, {
        ...(value !== undefined && { value, currency }),
    });
};

/**
 * Set user properties
 *
 * @param userId - User ID
 * @param userProperties - Additional user properties
 */
export const setUserProperties = (
    userId: string | number,
    userProperties?: Record<string, any>
): void => {
    if (isGtagAvailable()) {
        window.gtag('set', {
            user_id: String(userId),
            ...userProperties,
        });
    }
};
